// Wording for imports (005 FR-069, 006 FR-120), the same as the apps'
// shared TimelineImport, from i18n/common (spec 013 FR-010).
import type { TimelineImport } from '../api/types';
import { locale, t } from '../i18n/index.ts';

// IMPORT_FORMATS names what an import's file was, read from the catalog
// each time so it follows the language. An archive takes the format of the
// first file read from it.
export const IMPORT_FORMATS: Record<string, string> = Object.defineProperties(
  {} as Record<string, string>,
  Object.fromEntries(
    ['google_takeout_semantic', 'google_takeout_records', 'google_device_android', 'google_device_ios', 'gpx', 'geojson', 'google_fit_session', 'google_fit_daily', 'google_fit_data', 'tcx'].map(
      (format) => [format, { enumerable: true, get: () => t('importsFormat', { format }) }],
    ),
  ),
);

// IMPORT_ACCEPT is what the file picker offers: single exports, and a Google
// Takeout archive as a zip or a .tgz. The server goes by content, not name.
export const IMPORT_ACCEPT =
  '.json,.zip,.tgz,.gz,.gpx,.geojson,.tcx,application/json,application/zip,application/gzip,application/x-gzip,application/x-tar,application/gpx+xml,application/geo+json';

// ImportPart is what a page lists of the member's imports: the timeline's
// Imports tab the ones that read location history, and Settings, Activities
// the ones that read Google Fit workouts or health data (005 FR-076,
// 006 FR-126). An archive with both is on both, each with its own counts.
export type ImportPart = 'location' | 'fit';

// importAdded lists what an import has added so far to one part, or '' for
// nothing (006 FR-120, 005 FR-069).
export function importAdded(i: TimelineImport, part: ImportPart): string {
  const parts: string[] = [];
  const add = (n: number, words: (n: number) => string) => {
    if (n > 0) parts.push(words(n));
  };
  if (part === 'fit') {
    add(i.activities_added ?? 0, (n) => t('importsActivities', { n }));
    add(i.step_days_added ?? 0, (n) => t('importsStepDays', { n }));
    add(i.hr_hours_added ?? 0, (n) => t('importsHeartRateHours', { n }));
    add(i.resting_days_added ?? 0, (n) => t('importsRestingDays', { n }));
    add(i.sleep_added ?? 0, (n) => t('importsSleepNights', { n }));
    add(i.readings_added ?? 0, (n) => t('importsWeighIns', { n }));
  } else {
    add(i.stays_added, (n) => t('importsStays', { n }));
    add(i.trips_added, (n) => t('importsTrips', { n }));
  }
  return new Intl.ListFormat(locale(), { type: 'conjunction' }).format(parts);
}

// importSummary says what a finished import added to one part, and what it
// left out and why, one sentence each (006 FR-120, 005 FR-069). Location
// history that was not kept is told on both pages, since the member may have
// uploaded the archive from either.
export function importSummary(i: TimelineImport, part: ImportPart): string[] {
  const added = importAdded(i, part);
  const out = [added ? t('importsAdded', { items: added }) : t('importsNothingAdded')];
  if (part === 'fit') {
    if ((i.activities_skipped ?? 0) > 0) out.push(t('importsSkippedOverlap', { n: i.activities_skipped }));
    if ((i.activities_too_old ?? 0) > 0) out.push(t('importsWorkoutsTooOld', { n: i.activities_too_old }));
    if ((i.health_skipped ?? 0) > 0) out.push(t('importsHealthSkipped', { n: i.health_skipped }));
    if ((i.health_too_old ?? 0) > 0) out.push(t('importsHealthTooOld', { n: i.health_too_old }));
  } else {
    const places = i.stays_skipped + i.trips_skipped;
    if (places > 0) out.push(t('importsPlacesSkipped', { n: places }));
    if (i.days_too_old > 0) out.push(t('importsDaysTooOld', { n: i.days_too_old }));
  }
  if (i.timeline_off) out.push(t('importsTimelineOff'));
  if (part === 'fit' && i.height_cm != null) out.push(t('importsHeightSet'));
  return out;
}

// importProgress says how far a running import has got with one part. Days
// too old to keep are told before it finishes.
export function importProgress(i: TimelineImport, part: ImportPart): string {
  const added = importAdded(i, part);
  const reading = added ? t('importsReadingSoFar', { items: added }) : t('importsReading');
  if (part === 'location' && i.days_too_old > 0) return `${reading} ${t('importsDaysTooOld', { n: i.days_too_old })}`;
  return reading;
}
